import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Stethoscope, UtensilsCrossed, ShieldCheck } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Reveal } from "@/components/landing/Reveal";
import GuardianDonationWidget from "@/components/donation/GuardianDonationWidget";
import { setMetaTag } from "@/lib/seo";
import { CANONICAL_URL } from "@/lib/api";

const MEAL_MATH = [
  { amount: "₹25", detail: "One warm meal for a rescued dog" },
  { amount: "₹100/mo", detail: "4 dogs fed, every single month" },
  { amount: "₹500/mo", detail: "20 dogs fed, every single month" },
  { amount: "₹1,000/mo", detail: "40 dogs fed, every month — Guardian level", highlight: true },
];

const WHY_MONTHLY = [
  {
    icon: Calendar,
    title: "Predictable funding",
    body: "A steady base of monthly gifts means we can plan treatment schedules and supply orders weeks ahead, instead of reacting case by case.",
  },
  {
    icon: Stethoscope,
    title: "Vets we can keep",
    body: "Reliable monthly income means we can retain our veterinary team full-time, rather than losing them between one-off donation cycles.",
  },
  {
    icon: ShieldCheck,
    title: "Emergencies covered",
    body: "When a street dog is hit by a car at 2am, we can't wait for a fundraiser. Guardian gifts mean we already have the funds ready.",
  },
  {
    icon: UtensilsCrossed,
    title: "Fed every single day",
    body: "500+ animals in our care eat twice a day, every day of the year. Monthly giving is what makes that dependable, not occasional.",
  },
];

export default function Guardians() {
  useEffect(() => {
    document.title = "Become an AMTM Guardian | AMTM Rescue Hub";
    setMetaTag("og:title", "Become an AMTM Guardian | AMTM Rescue Hub");
    setMetaTag("og:description", "Guardians give monthly, so we can plan ahead, keep our vets, and never turn away an emergency.");
    setMetaTag("og:image", `${CANONICAL_URL}/images/sanctuary.jpg`);
  }, []);

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="guardians-page">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-giveText mb-5">
            AMTM Guardians
          </p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-amtm-text mb-5" data-testid="guardians-page-headline">
            Become an AMTM Guardian
          </h1>
          <p className="text-base md:text-lg text-amtm-textMuted leading-relaxed">
            Guardians give monthly, so we can plan ahead, keep our vets, and never turn away an emergency.
          </p>
        </div>
        <div>
          <GuardianDonationWidget testIdPrefix="guardians-page" />
          <p className="text-xs text-amtm-textMuted text-center mt-3" data-testid="guardians-page-cancel-subtext">
            Not sure? You can pause or cancel your monthly gift anytime from the{" "}
            <Link to="/my-giving" className="text-amtm-teal font-medium hover:underline">
              My Giving
            </Link>{" "}
            page — no questions asked.
          </p>
        </div>
      </section>

      <section className="bg-amtm-give/20 py-20 md:py-28" data-testid="guardians-meal-math-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-12 max-w-xl mx-auto">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-giveText mb-4">
              Where It Goes
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-amtm-text">What your monthly gift does.</h2>
          </Reveal>
          <img
            src="/images/dog-meal.jpg"
            alt="A rescued dog eating a warm meal at AMTM"
            width="1600"
            height="900"
            loading="lazy"
            data-testid="guardians-meal-image"
            className="w-full h-64 sm:h-72 md:h-auto md:aspect-video object-cover object-[42%_45%] md:object-center rounded-2xl mb-10 md:mb-12 shadow-sm"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {MEAL_MATH.map((m) => (
              <div
                key={m.amount}
                data-testid={`meal-math-card-${m.amount.replace(/[^0-9]/g, "")}`}
                className={`rounded-2xl border p-5 text-center ${
                  m.highlight ? "border-amtm-teal bg-amtm-teal/[0.06] ring-1 ring-amtm-teal" : "border-amtm-border bg-white"
                }`}
              >
                <p className="font-display text-2xl text-amtm-text mb-1.5">{m.amount}</p>
                <p className="text-sm text-amtm-textMuted">{m.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="guardians-why-monthly-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-14 max-w-xl mx-auto">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-giveText mb-4">
              Why Monthly
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-amtm-text">Why monthly matters.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            {WHY_MONTHLY.map((w) => (
              <div key={w.title} className="bg-white border border-amtm-border rounded-2xl p-6" data-testid={`why-monthly-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="h-11 w-11 rounded-xl bg-amtm-give/25 flex items-center justify-center mb-4">
                  <w.icon className="h-5 w-5 text-amtm-giveText" />
                </div>
                <h3 className="font-display text-lg text-amtm-text mb-1.5">{w.title}</h3>
                <p className="text-sm text-amtm-textMuted leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
